import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Plus,
  Edit3,
  Trash2,
  RefreshCw,
  Sparkles,
  Save,
  AlertTriangle,
  Bot,
  Target,
  Wand2,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  card,
  pageTitle,
  microLabel,
  microLabelWide,
  sectionTitle,
  accentBarSection,
  bodySecondary,
  motionFadeUp,
  motionStaggerChild,
  emptyState,
  iconSize,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";
import {
  getCOPOMatrix,
  deleteCO,
  bulkUpsertMappings,
  suggestCOs,
  suggestCoPoJustification,
  getEndTermConfig,
  saveEndTermConfig,
} from "../../../../services/program.service";
import CoForm from "./CoForm";
import AIQuizForm from "./AIQuizForm";
import AISuggestCOsPreview from "./AISuggestCOsPreview";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const BLOOM_LABEL = {
  remember: "Remember",
  understand: "Understand",
  apply: "Apply",
  analyze: "Analyze",
  evaluate: "Evaluate",
  create: "Create",
};

const cellKey = (coId, type, id) => `${coId}:${type}:${id}`;

// Build a quick lookup of mappings keyed by (coId, type, outcomeId)
const buildMappingIndex = (mappings) => {
  const index = {};
  for (const m of mappings) {
    index[cellKey(m.co, m.outcomeType, m.outcomeId)] = m;
  }
  return index;
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CourseOutcomesView = ({ courseId, courseCode, onBack }) => {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // CO form state
  const [showCoForm, setShowCoForm] = useState(false);
  const [editingCo, setEditingCo] = useState(null);

  // AI Quiz modal state (Phase 3d)
  const [showQuizForm, setShowQuizForm] = useState(false);

  // AI-suggest COs review modal
  const [aiCoSuggestions, setAiCoSuggestions] = useState(null); // { items, isMock } | null

  // AI
  const [aiCellLoading, setAiCellLoading] = useState(null); // cellKey string
  const [generatingCOs, setGeneratingCOs] = useState(false);

  // Local matrix state (dirty tracking)
  const [localCells, setLocalCells] = useState({}); // cellKey → { strength, justification, isAiSuggested }
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Exam CO allocation state (Phase 1.2)
  const [examConfig, setExamConfig] = useState(null); // { endTermMaxMarks, midTermMaxMarks, coAllocations }
  const [examDirty, setExamDirty] = useState(false);
  const [examSaving, setExamSaving] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [data, examData] = await Promise.all([
        getCOPOMatrix(courseId),
        getEndTermConfig(courseId).catch(() => null),
      ]);
      setMatrix(data);
      if (examData) {
        setExamConfig(examData);
        setExamDirty(false);
      }
      // Initialise local cells from saved mappings
      const seed = {};
      for (const m of data.mappings || []) {
        seed[cellKey(m.co, m.outcomeType, m.outcomeId)] = {
          strength: m.strength,
          justification: m.justification || "",
          isAiSuggested: !!m.isAiSuggested,
        };
      }
      setLocalCells(seed);
      setDirty(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // ---------- CO actions ----------

  const handleAddCO = () => {
    setEditingCo(null);
    setShowCoForm(true);
  };
  const handleEditCO = (co) => {
    setEditingCo(co);
    setShowCoForm(true);
  };
  const handleDeleteCO = async (co) => {
    if (
      !window.confirm(
        `Delete ${co.code}? This is a soft delete and existing mappings to it will become orphans.`
      )
    )
      return;
    try {
      await deleteCO(co._id);
      toast.success(`${co.code} deleted`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  const handleSuggestCOs = async () => {
    setGeneratingCOs(true);
    try {
      const suggestion = await suggestCOs({
        course_code: courseCode || matrix?.course?.courseCode,
        num_cos: 5,
      });
      const items = suggestion.outcomes || [];
      if (items.length === 0) {
        toast.error("AI returned no outcomes. Try again.");
        return;
      }
      // Map AI response → editable preview items; the user reviews and
      // accepts (or edits) each one before anything is saved.
      const mapped = items.map((o, i) => ({
        code: o.code || `CO${i + 1}`,
        statement: o.statement || "",
        bloomLevel: (o.bloom_level || o.bloomLevel || "").toLowerCase(),
        topics: o.topics || [],
        order: i + 1,
      }));
      setAiCoSuggestions({ items: mapped, isMock: !!suggestion._mock });
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "AI suggestion failed — is the Python service running?"
      );
    } finally {
      setGeneratingCOs(false);
    }
  };

  // ---------- Matrix actions ----------

  const setCellStrength = (coId, type, id, strength) => {
    const key = cellKey(coId, type, id);
    setLocalCells((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { justification: "", isAiSuggested: false }),
        strength,
      },
    }));
    setDirty(true);
  };

  const handleCellAI = async (co, outcomeType, outcomeObj) => {
    const key = cellKey(co._id, outcomeType, outcomeObj._id);
    setAiCellLoading(key);
    try {
      const result = await suggestCoPoJustification({
        co_statement: co.statement,
        co_bloom_level: co.bloomLevel || "",
        po_statement: outcomeObj.statement,
        po_category: outcomeObj.category || "",
      });
      setLocalCells((prev) => ({
        ...prev,
        [key]: {
          strength: result.strength,
          justification: result.justification || "",
          isAiSuggested: true,
        },
      }));
      setDirty(true);
      if (result._mock) {
        toast(
          `Demo strength ${result.strength} for ${co.code} → ${outcomeObj.code}`,
          { icon: "🧪", duration: 3000 }
        );
      } else {
        toast.success(
          `AI: strength ${result.strength} for ${co.code} → ${outcomeObj.code}`
        );
      }
    } catch (err) {
      toast.error(
        err.response?.data?.error || "AI suggest failed — is Python running?"
      );
    } finally {
      setAiCellLoading(null);
    }
  };

  const handleSaveMatrix = async () => {
    if (!matrix) return;
    setSaving(true);
    try {
      const mappings = [];
      for (const co of matrix.outcomes) {
        for (const po of matrix.pos) {
          const c = localCells[cellKey(co._id, "PO", po._id)];
          if (c && c.strength != null && c.strength > 0) {
            mappings.push({
              co: co._id,
              outcomeType: "PO",
              outcomeId: po._id,
              strength: c.strength,
              justification: c.justification || "",
              isAiSuggested: !!c.isAiSuggested,
            });
          }
        }
        for (const pso of matrix.psos) {
          const c = localCells[cellKey(co._id, "PSO", pso._id)];
          if (c && c.strength != null && c.strength > 0) {
            mappings.push({
              co: co._id,
              outcomeType: "PSO",
              outcomeId: pso._id,
              strength: c.strength,
              justification: c.justification || "",
              isAiSuggested: !!c.isAiSuggested,
            });
          }
        }
      }
      const result = await bulkUpsertMappings(courseId, mappings, true);
      toast.success(
        `Saved · ${result.upserted + result.modified} mapping(s) · ${result.deleted} cleared`
      );
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Render ----------

  if (loading) {
    return (
      <div className="p-4 sm:p-8 lg:p-10 pt-16 lg:pt-10 min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
          <div className="h-8 w-2/3 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
          <div className="h-1 w-12 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
          <div className="h-48 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
          <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
        </div>
      </div>
    );
  }

  if (error || !matrix) {
    return (
      <div className="p-4 sm:p-8 lg:p-10 pt-16 lg:pt-10 min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          {onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-6"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}
          <div className="p-6 rounded-sm border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
            {error || "Course not found"}
          </div>
        </div>
      </div>
    );
  }

  const { course, program, outcomes, pos, psos, warnings } = matrix;

  return (
    <div className="p-4 sm:p-8 lg:p-10 pt-16 lg:pt-10 min-h-screen bg-gray-50 dark:bg-gray-900">
      <motion.div {...motionFadeUp} className="max-w-7xl mx-auto">
        {/* Back link — only when onBack is supplied (admin nav). In the
            teacher's CourseManagement tab the parent tab strip is the
            navigation, so onBack is omitted. */}
        {onBack && (
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-accent1 transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> Back to program
          </button>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className={microLabelWide}>
              {course.courseCode}
              {program ? ` · ${program.code}` : ""}
            </div>
            <h1 className={cn(pageTitle, "text-3xl sm:text-4xl mt-1")}>
              {course.title}
            </h1>
            <div className={cn(accentBarSection, "mt-4")} />
            <p className={cn(bodySecondary, "mt-3 max-w-2xl")}>
              Author Course Outcomes for this course, then map each to the
              program's POs / PSOs. AI-assist available end-to-end — the
              Python service uses your course PDFs (from{" "}
              <code className="text-xs">/upload</code>) as context.
            </p>
          </div>
          <button
            onClick={refresh}
            className="self-start px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className={iconSize.md} />
          </button>
        </div>

        {/* Warnings */}
        {warnings && warnings.length > 0 && (
          <div className="mt-6 space-y-2">
            {warnings.map((w, i) => (
              <div
                key={i}
                className="flex items-start gap-2 p-3 rounded-sm border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm"
              >
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}

        {/* Section: Course Outcomes */}
        <section className="mt-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
            <div>
              <h2 className={cn(sectionTitle, "text-xl sm:text-2xl")}>
                Course Outcomes
              </h2>
              <div className={cn(accentBarSection, "mt-2")} />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSuggestCOs}
                disabled={generatingCOs}
                className={cn(
                  "inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-accent1 text-accent1 font-semibold transition-colors",
                  generatingCOs
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:bg-accent2"
                )}
              >
                <Sparkles className={iconSize.md} />
                {generatingCOs ? "Generating…" : "Suggest 5 COs (AI)"}
              </button>
              <button
                onClick={() => setShowQuizForm(true)}
                disabled={outcomes.length === 0}
                className={cn(
                  "inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-accent1 text-accent1 font-semibold transition-colors",
                  outcomes.length === 0
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:bg-accent2"
                )}
                title={
                  outcomes.length === 0
                    ? "Define COs first"
                    : "Generate a draft Assignment with AI-drafted questions tagged to your COs"
                }
              >
                <Wand2 className={iconSize.md} />
                Generate Quiz
              </button>
              <button
                onClick={handleAddCO}
                className="inline-flex items-center gap-2 bg-accent1 text-white font-semibold px-4 py-2 rounded-sm hover:opacity-90 transition-opacity"
              >
                <Plus className={iconSize.md} />
                Add CO
              </button>
            </div>
          </div>

          {outcomes.length === 0 ? (
            <div className={emptyState}>
              <div className="mx-auto h-14 w-14 rounded-sm bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                <Target className="h-7 w-7 text-gray-400" />
              </div>
              <div className="font-bold text-gray-700 dark:text-gray-200">
                No Course Outcomes yet
              </div>
              <div className={cn(bodySecondary, "mt-1")}>
                Click "Suggest 5 COs (AI)" to draft a starter set from your
                uploaded PDFs, or add manually.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {outcomes.map((co, i) => (
                <motion.div
                  key={co._id}
                  {...motionStaggerChild(i, 0)}
                  className={cn(card, "p-5 group")}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="h-1 w-8 bg-accent1 rounded-sm mb-2" />
                      <div className={microLabel}>{co.code}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {co.bloomLevel && (
                          <span className="px-2.5 py-1 rounded-sm bg-accent2 text-accent1 text-xs font-semibold">
                            {BLOOM_LABEL[co.bloomLevel] || co.bloomLevel}
                          </span>
                        )}
                        {co.migratedFromLegacy && (
                          <span className="px-2.5 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium">
                            Migrated
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                        {co.statement}
                      </p>
                      {co.topics && co.topics.length > 0 && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          Topics: {co.topics.join(", ")}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button
                        onClick={() => handleEditCO(co)}
                        className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Edit"
                        aria-label="Edit"
                      >
                        <Edit3 className={iconSize.sm} />
                      </button>
                      <button
                        onClick={() => handleDeleteCO(co)}
                        className="p-2 rounded-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600"
                        title="Delete"
                        aria-label="Delete"
                      >
                        <Trash2 className={iconSize.sm} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* Section: CO-PO Matrix */}
        <section className="mt-12">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
            <div>
              <h2 className={cn(sectionTitle, "text-xl sm:text-2xl")}>
                CO-PO / PSO Mapping Matrix
              </h2>
              <div className={cn(accentBarSection, "mt-2")} />
              <p className={cn(bodySecondary, "mt-3 max-w-2xl")}>
                Strength: 1 (slight) · 2 (moderate) · 3 (substantial). Click the
                bot icon in a cell to AI-suggest strength + justification for
                that specific CO-PO pair.
              </p>
            </div>
            <button
              onClick={handleSaveMatrix}
              disabled={saving || !dirty}
              className={cn(
                "inline-flex items-center gap-2 px-5 py-2 rounded-sm bg-accent1 text-white font-semibold transition-opacity",
                saving || !dirty
                  ? "opacity-60 cursor-not-allowed"
                  : "hover:opacity-90"
              )}
            >
              <Save className={iconSize.md} />
              {saving
                ? "Saving…"
                : dirty
                ? "Save matrix"
                : "Saved"}
            </button>
          </div>

          {outcomes.length === 0 ||
          (pos.length === 0 && psos.length === 0) ? (
            <div className={emptyState}>
              <div className="font-bold text-gray-700 dark:text-gray-200">
                Cannot show matrix yet
              </div>
              <div className={cn(bodySecondary, "mt-1")}>
                Need at least one CO above and the program's POs/PSOs to be
                seeded (Admin → Programs → POs tab).
              </div>
            </div>
          ) : (
            <MatrixTable
              outcomes={outcomes}
              pos={pos}
              psos={psos}
              localCells={localCells}
              aiCellLoading={aiCellLoading}
              onChangeStrength={setCellStrength}
              onCellAI={handleCellAI}
            />
          )}
        </section>

        {/* ── Exam CO Allocation ─────────────────────────────────────────── */}
        {examConfig && examConfig.coAllocations && examConfig.coAllocations.length > 0 && (
          <section className={cn(card, "p-6 mt-10")}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={sectionTitle}>Exam CO Allocation</h3>
                <p className={cn(bodySecondary, "mt-0.5 text-xs")}>
                  Set what % of end-term and mid-term marks each CO covers — used in attainment calculation.
                </p>
              </div>
              <button
                onClick={async () => {
                  if (!examDirty) return;
                  setExamSaving(true);
                  try {
                    await saveEndTermConfig(courseId, {
                      endTermMaxMarks: examConfig.endTermMaxMarks,
                      midTermMaxMarks: examConfig.midTermMaxMarks,
                      coAllocations: examConfig.coAllocations.map((a) => ({
                        co: a.co._id,
                        endTermPct: Number(a.endTermPct) || 0,
                        midTermPct: Number(a.midTermPct) || 0,
                      })),
                    });
                    setExamDirty(false);
                    toast.success("Exam allocation saved");
                  } catch (err) {
                    toast.error(err.response?.data?.error || "Save failed");
                  } finally {
                    setExamSaving(false);
                  }
                }}
                disabled={!examDirty || examSaving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-accent1 text-white text-sm font-medium hover:bg-accent1/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4" />
                {examSaving ? "Saving…" : "Save Allocation"}
              </button>
            </div>

            {/* Max marks row */}
            <div className="flex gap-6 mb-5 p-3 rounded-sm bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <span className={microLabel}>End-Term Max Marks</span>
                <input
                  type="number"
                  min="0"
                  value={examConfig.endTermMaxMarks}
                  onChange={(e) => {
                    setExamConfig((c) => ({ ...c, endTermMaxMarks: Number(e.target.value) }));
                    setExamDirty(true);
                  }}
                  className="w-20 px-2 py-1 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-center"
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <span className={microLabel}>Mid-Term Max Marks</span>
                <input
                  type="number"
                  min="0"
                  value={examConfig.midTermMaxMarks}
                  onChange={(e) => {
                    setExamConfig((c) => ({ ...c, midTermMaxMarks: Number(e.target.value) }));
                    setExamDirty(true);
                  }}
                  className="w-20 px-2 py-1 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-center"
                />
              </label>
            </div>

            {/* Allocation table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 pr-4 font-semibold text-gray-700 dark:text-gray-300 w-1/2">Course Outcome</th>
                    <th className="text-center py-2 px-3 font-semibold text-gray-700 dark:text-gray-300">End-Term %</th>
                    <th className="text-center py-2 px-3 font-semibold text-gray-700 dark:text-gray-300">Mid-Term %</th>
                  </tr>
                </thead>
                <tbody>
                  {examConfig.coAllocations.map((alloc, idx) => (
                    <tr key={alloc.co._id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/30">
                      <td className="py-2 pr-4">
                        <span className="font-bold text-accent1 mr-2">{alloc.co.code}</span>
                        <span className="text-gray-600 dark:text-gray-400 text-xs">{alloc.co.statement}</span>
                      </td>
                      <td className="py-2 px-3 text-center">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={alloc.endTermPct}
                          onChange={(e) => {
                            const val = Math.min(100, Math.max(0, Number(e.target.value)));
                            setExamConfig((c) => ({
                              ...c,
                              coAllocations: c.coAllocations.map((a, i) =>
                                i === idx ? { ...a, endTermPct: val } : a
                              ),
                            }));
                            setExamDirty(true);
                          }}
                          className="w-16 px-2 py-1 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-center"
                        />
                      </td>
                      <td className="py-2 px-3 text-center">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={alloc.midTermPct}
                          onChange={(e) => {
                            const val = Math.min(100, Math.max(0, Number(e.target.value)));
                            setExamConfig((c) => ({
                              ...c,
                              coAllocations: c.coAllocations.map((a, i) =>
                                i === idx ? { ...a, midTermPct: val } : a
                              ),
                            }));
                            setExamDirty(true);
                          }}
                          className="w-16 px-2 py-1 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-center"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600">
                    <td className="py-2 pr-4 font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">Total</td>
                    <td className="py-2 px-3 text-center">
                      <span className={cn(
                        "font-bold text-sm",
                        examConfig.coAllocations.reduce((s, a) => s + (a.endTermPct || 0), 0) === 100
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-amber-600 dark:text-amber-400"
                      )}>
                        {examConfig.coAllocations.reduce((s, a) => s + (a.endTermPct || 0), 0)}%
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className={cn(
                        "font-bold text-sm",
                        examConfig.coAllocations.reduce((s, a) => s + (a.midTermPct || 0), 0) === 100
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-amber-600 dark:text-amber-400"
                      )}>
                        {examConfig.coAllocations.reduce((s, a) => s + (a.midTermPct || 0), 0)}%
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className={cn(bodySecondary, "text-xs mt-3")}>
              Totals should add up to 100% per exam type. Green = 100%, amber = other value.
            </p>
          </section>
        )}
      </motion.div>

      {showCoForm && (
        <CoForm
          courseId={courseId}
          co={editingCo}
          onClose={(reload) => {
            setShowCoForm(false);
            setEditingCo(null);
            if (reload) refresh();
          }}
        />
      )}

      {showQuizForm && (
        <AIQuizForm
          courseId={courseId}
          courseCode={courseCode || matrix?.course?.courseCode}
          outcomes={outcomes}
          onClose={() => setShowQuizForm(false)}
        />
      )}

      {aiCoSuggestions && (
        <AISuggestCOsPreview
          courseId={courseId}
          suggestions={aiCoSuggestions.items}
          existingCodes={outcomes.map((o) => o.code)}
          isMock={aiCoSuggestions.isMock}
          onClose={(reload) => {
            setAiCoSuggestions(null);
            if (reload) refresh();
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Matrix table — separate sub-component for readability
// ---------------------------------------------------------------------------

const STRENGTHS = [
  { value: 0, label: "—" },
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
];

const MatrixTable = ({
  outcomes,
  pos,
  psos,
  localCells,
  aiCellLoading,
  onChangeStrength,
  onCellAI,
}) => (
  <div className={cn(card, "overflow-x-auto")}>
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200 dark:border-gray-700">
          <th className="sticky left-0 z-10 bg-white dark:bg-gray-800 text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 min-w-[100px]">
            CO ↓ &nbsp; PO/PSO →
          </th>
          {pos.map((po) => (
            <th
              key={po._id}
              className="px-3 py-3 text-center text-xs font-bold text-gray-700 dark:text-gray-300 whitespace-nowrap"
              title={po.statement}
            >
              <div>{po.code}</div>
              {po.category && (
                <div className="text-[10px] text-gray-400 font-normal max-w-[120px] truncate">
                  {po.category}
                </div>
              )}
            </th>
          ))}
          {psos.map((pso) => (
            <th
              key={pso._id}
              className="px-3 py-3 text-center text-xs font-bold text-accent1 whitespace-nowrap bg-accent2/30"
              title={pso.statement}
            >
              {pso.code}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {outcomes.map((co) => (
          <tr
            key={co._id}
            className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/30"
          >
            <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-4 py-2 text-xs font-bold text-gray-900 dark:text-white min-w-[100px]">
              <div>{co.code}</div>
              {co.bloomLevel && (
                <div className="text-[10px] text-accent1 font-semibold uppercase tracking-wider mt-0.5">
                  {BLOOM_LABEL[co.bloomLevel] || co.bloomLevel}
                </div>
              )}
            </td>
            {[...pos.map((p) => ({ ...p, _type: "PO" })), ...psos.map((p) => ({ ...p, _type: "PSO" }))].map(
              (outcomeObj) => {
                const key = cellKey(co._id, outcomeObj._type, outcomeObj._id);
                const cell = localCells[key];
                const strength = cell?.strength ?? 0;
                const isAiSuggested = !!cell?.isAiSuggested;
                const isPso = outcomeObj._type === "PSO";
                return (
                  <td
                    key={key}
                    className={cn(
                      "px-2 py-2 text-center align-middle",
                      isPso && "bg-accent2/20"
                    )}
                  >
                    <div className="inline-flex items-center gap-1">
                      <select
                        value={strength}
                        onChange={(e) =>
                          onChangeStrength(
                            co._id,
                            outcomeObj._type,
                            outcomeObj._id,
                            Number(e.target.value)
                          )
                        }
                        className={cn(
                          "px-2 py-1 text-sm rounded-sm border focus:outline-none focus:border-accent1 transition-colors",
                          strength === 0
                            ? "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-400"
                            : strength === 3
                            ? "border-accent1 bg-accent2 text-accent1 font-bold"
                            : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 font-semibold"
                        )}
                      >
                        {STRENGTHS.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => onCellAI(co, outcomeObj._type, outcomeObj)}
                        disabled={aiCellLoading === key}
                        className={cn(
                          "p-1 rounded-sm transition-colors",
                          aiCellLoading === key
                            ? "opacity-60 cursor-not-allowed"
                            : "hover:bg-accent2 text-gray-400 hover:text-accent1"
                        )}
                        title="AI suggest strength + justification for this pair"
                        aria-label="AI suggest"
                      >
                        <Bot className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {isAiSuggested && strength > 0 && (
                      <div className="text-[9px] uppercase tracking-wider text-accent1 font-bold mt-0.5">
                        AI
                      </div>
                    )}
                  </td>
                );
              }
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default CourseOutcomesView;
